package com.siriusxm.transcripts

/**
 * Parses WebVTT formatted text into a structured VTTDocument.
 *
 * WebVTT format:
 * ```
 * WEBVTT
 *
 * 00:00:00.000 --> 00:00:03.500
 * First subtitle line
 *
 * 00:00:03.500 --> 00:00:07.000
 * Second subtitle line
 * Can be multi-line
 * ```
 *
 * @param text The raw VTT text content
 * @return A VTTDocument containing parsed cues
 */
fun parseVTT(text: String): VTTDocument {
    val cues = mutableListOf<VTTCue>()
    val lines = text.lines()
    var index = 0
    var i = 0

    // Skip the WEBVTT header
    while (i < lines.size && !lines[i].trim().startsWith("WEBVTT")) {
        i++
    }
    i++ // Skip the WEBVTT line itself

    while (i < lines.size) {
        val line = lines[i].trim()

        // Skip empty lines and cue identifiers (optional numbers)
        if (line.isEmpty() || line.toIntOrNull() != null) {
            i++
            continue
        }

        // Check if this is a timestamp line (HH:MM:SS.mmm --> HH:MM:SS.mmm)
        val timestampRegex = """(\d{2}:\d{2}:\d{2}\.\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}\.\d{3})""".toRegex()
        val match = timestampRegex.find(line)

        if (match != null) {
            val startTime = parseTimestamp(match.groupValues[1])
            val endTime = parseTimestamp(match.groupValues[2])

            // Collect the text lines until we hit an empty line or another timestamp
            val textLines = mutableListOf<String>()
            i++
            while (i < lines.size) {
                val textLine = lines[i].trim()
                if (textLine.isEmpty() || timestampRegex.containsMatchIn(textLine)) {
                    break
                }
                textLines.add(textLine)
                i++
            }

            val rawText = textLines.joinToString("\n")

            // Extract speaker annotation if present (e.g., <v Speaker 3>)
            val speakerRegex = """<v\s+([^>]+)>""".toRegex()
            val speakerMatch = speakerRegex.find(rawText)
            val speaker = speakerMatch?.groupValues?.get(1)

            // Remove both opening and closing voice tags
            val text = rawText
                .replace(speakerRegex, "")
                .replace("</v>", "")
                .trim()

            cues.add(VTTCue(startTime, endTime, text, index, speaker))
            index++
        } else {
            i++
        }
    }

    return VTTDocument(cues, text)
}

/**
 * Parses a VTT timestamp (HH:MM:SS.mmm) into milliseconds.
 *
 * @param timestamp The timestamp string (e.g., "00:01:23.456")
 * @return The time in milliseconds (e.g., 83456)
 */
private fun parseTimestamp(timestamp: String): Long {
    val parts = timestamp.split(":")
    if (parts.size != 3) return 0L

    val hours = parts[0].toIntOrNull() ?: 0
    val minutes = parts[1].toIntOrNull() ?: 0
    val secondsParts = parts[2].split(".")
    val seconds = secondsParts[0].toIntOrNull() ?: 0
    val milliseconds = if (secondsParts.size > 1) secondsParts[1].toIntOrNull() ?: 0 else 0

    return (hours * 3600L + minutes * 60L + seconds) * 1000L + milliseconds
}
